/**
 * shadcn/ui Color Picker (Ark UI) — https://www.shadcn.io/ui/color-picker
 * Styled primitives aligned with the shadcn Ark UI registry component.
 */
import { ColorPicker as ColorPickerPrimitive, parseColor } from "@ark-ui/react/color-picker";
import { Portal } from "@ark-ui/react/portal";
import { CheckIcon, EyedropperIcon } from "@phosphor-icons/react";
import { cn } from "@bafe/ui";
import type { ComponentProps, ReactNode } from "react";

export {
	parseColor,
	useColorPicker,
	useColorPickerContext,
} from "@ark-ui/react/color-picker";

export type { ColorPickerValueChangeDetails as ValueChangeDetails } from "@ark-ui/react/color-picker";

const thumbRing =
	"size-3.5 rounded-full shadow-[0_0_0_2px_white,0_0_0_3px_rgba(0,0,0,0.15)] outline-none";

const rootLayoutClass = "flex w-full max-w-64 flex-col gap-2";

export const ColorPicker = ({
	className,
	children,
	...props
}: ColorPickerPrimitive.RootProps) => (
	<ColorPickerPrimitive.Root
		data-slot="color-picker"
		className={cn(rootLayoutClass, className)}
		{...props}
	>
		{children}
		<ColorPickerPrimitive.HiddenInput />
	</ColorPickerPrimitive.Root>
);

export const ColorPickerRootProvider = ({
	className,
	children,
	...props
}: ColorPickerPrimitive.RootProviderProps) => (
	<ColorPickerPrimitive.RootProvider
		data-slot="color-picker"
		className={cn(rootLayoutClass, className)}
		{...props}
	>
		{children}
		<ColorPickerPrimitive.HiddenInput />
	</ColorPickerPrimitive.RootProvider>
);

export const ColorPickerLabel = ({
	className,
	...props
}: ColorPickerPrimitive.LabelProps) => (
	<ColorPickerPrimitive.Label
		data-slot="color-picker-label"
		className={cn("text-sm font-medium text-(--text-primary) select-none data-disabled:opacity-50", className)}
		{...props}
	/>
);

export const ColorPickerControl = ({
	className,
	...props
}: ColorPickerPrimitive.ControlProps) => (
	<ColorPickerPrimitive.Control
		data-slot="color-picker-control"
		className={cn("flex items-center gap-2", className)}
		{...props}
	/>
);

export const ColorPickerTriggerSwatch = ({
	className,
	...props
}: ComponentProps<"div">) => (
	<div
		data-slot="color-picker-trigger-swatch"
		className={cn("relative grid size-full place-items-center", className)}
		{...props}
	>
		<ColorPickerPrimitive.TransparencyGrid className="col-start-1 row-start-1 size-full rounded-[inherit]" />
		<ColorPickerPrimitive.ValueSwatch className="col-start-1 row-start-1 z-1 size-full rounded-[inherit]" />
	</div>
);

export const ColorPickerTrigger = ({
	className,
	children,
	...props
}: ColorPickerPrimitive.TriggerProps) => (
	<ColorPickerPrimitive.Trigger
		data-slot="color-picker-trigger"
		className={cn(
			"grid size-9 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-lg border border-(--border-subtle) bg-(--surface-raised) p-0 shadow-(--shadow-subtle) transition-colors data-disabled:cursor-not-allowed data-disabled:opacity-50 sm:size-8",
			className,
		)}
		{...props}
	>
		{children ?? <ColorPickerTriggerSwatch />}
	</ColorPickerPrimitive.Trigger>
);

export const ColorPickerPositioner = ({
	className,
	children,
	...props
}: ColorPickerPrimitive.PositionerProps) => (
	<Portal>
		<ColorPickerPrimitive.Positioner
			data-slot="color-picker-positioner"
			className={cn(className)}
			{...props}
		>
			{children}
		</ColorPickerPrimitive.Positioner>
	</Portal>
);

export const ColorPickerContent = ({
	className,
	...props
}: ColorPickerPrimitive.ContentProps) => (
	<ColorPickerPrimitive.Content
		data-slot="color-picker-content"
		className={cn(
			"flex z-50 w-64 flex-col gap-3 rounded-lg border border-(--border-subtle) bg-(--surface-raised) p-4 shadow-(--shadow-overlay) outline-none origin-(--transform-origin) data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95",
			className,
		)}
		{...props}
	/>
);

export const ColorPickerArea = ({
	className,
	...props
}: ColorPickerPrimitive.AreaProps) => (
	<ColorPickerPrimitive.Area
		data-slot="color-picker-area"
		className={cn("relative h-40 overflow-hidden rounded-md touch-none", className)}
		{...props}
	>
		<ColorPickerPrimitive.AreaBackground className="size-full rounded-[inherit]" />
		<ColorPickerPrimitive.AreaThumb className={thumbRing} />
	</ColorPickerPrimitive.Area>
);

const channelSliderDefaultTrack = (channel: string) => (
	<>
		{channel === "alpha" && (
			<ColorPickerPrimitive.TransparencyGrid className="absolute inset-0 rounded-[inherit]" />
		)}
		<ColorPickerPrimitive.ChannelSliderTrack className="h-2.5 w-full rounded-[inherit]" />
		<ColorPickerPrimitive.ChannelSliderThumb className={cn("size-3 rounded-full", thumbRing)} />
	</>
);

export const ColorPickerChannelSlider = ({
	className,
	channel,
	children,
	...props
}: ColorPickerPrimitive.ChannelSliderProps) => {
	const customLayout = children != null;
	return (
		<ColorPickerPrimitive.ChannelSlider
			data-slot="color-picker-channel-slider"
			channel={channel}
			className={cn(!customLayout && "relative h-2.5 flex-1 rounded", className)}
			{...props}
		>
			{customLayout ? children : channelSliderDefaultTrack(channel)}
		</ColorPickerPrimitive.ChannelSlider>
	);
};

/** shadcn docs name for {@link ColorPickerChannelSlider}. */
export const ColorPickerSlider = ColorPickerChannelSlider;

export const ColorPickerChannelInput = ({
	className,
	channel,
	...props
}: ColorPickerPrimitive.ChannelInputProps) => (
	<ColorPickerPrimitive.ChannelInput
		data-slot="color-picker-channel-input"
		channel={channel}
		className={cn(
			"h-9 min-w-0 flex-1 rounded-lg border border-(--border-subtle) bg-transparent px-2 text-center text-sm tabular-nums shadow-(--shadow-subtle) transition-colors [appearance:textfield] placeholder:text-(--text-muted) focus-visible:border-(--border-strong) focus-visible:ring-[3px] focus-visible:ring-(--focus-ring)/20 data-disabled:cursor-not-allowed data-disabled:opacity-50 sm:h-8 sm:text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
			channel === "alpha" && "w-16 min-w-[unset]",
			className,
		)}
		{...props}
	/>
);

/** shadcn docs name for {@link ColorPickerChannelInput}. */
export const ColorPickerInput = ColorPickerChannelInput;

export const ColorPickerEyeDropperTrigger = ({
	className,
	children,
	...props
}: ColorPickerPrimitive.EyeDropperTriggerProps) => (
	<ColorPickerPrimitive.EyeDropperTrigger
		data-slot="color-picker-eye-dropper-trigger"
		className={cn(
			"inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-(--border-subtle) bg-transparent text-(--text-primary) transition-colors hover:bg-(--surface-sunken) data-disabled:cursor-not-allowed data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
			className,
		)}
		{...props}
	>
		{children ?? <EyedropperIcon aria-hidden="true" />}
	</ColorPickerPrimitive.EyeDropperTrigger>
);

export type ColorPickerHueAlphaSlidersProps = {
	className?: string;
	eyeDropperProps?: ComponentProps<typeof ColorPickerEyeDropperTrigger>;
	slidersClassName?: string;
};

export const ColorPickerHueAlphaSliders = ({
	className,
	eyeDropperProps,
	slidersClassName,
}: ColorPickerHueAlphaSlidersProps) => (
	<div className={cn("flex items-center gap-3", className)}>
		<ColorPickerEyeDropperTrigger {...eyeDropperProps} />
		<div className={cn("flex flex-1 flex-col gap-2.5", slidersClassName)}>
			<ColorPickerChannelSlider channel="hue" />
			<ColorPickerChannelSlider channel="alpha" />
		</div>
	</div>
);

export const ColorPickerSwatchGroup = ({
	className,
	...props
}: ComponentProps<typeof ColorPickerPrimitive.SwatchGroup>) => (
	<ColorPickerPrimitive.SwatchGroup
		data-slot="color-picker-swatch-group"
		className={cn("flex flex-wrap gap-2", className)}
		{...props}
	/>
);

export const ColorPickerSwatchTrigger = ({
	className,
	children,
	...props
}: ComponentProps<typeof ColorPickerPrimitive.SwatchTrigger>) => (
	<ColorPickerPrimitive.SwatchTrigger
		data-slot="color-picker-swatch-trigger"
		className={cn(
			"cursor-pointer rounded-full border-none bg-transparent p-0 outline-none transition-transform focus-visible:ring-2 focus-visible:ring-(--focus-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--surface)",
			className,
		)}
		{...props}
	>
		{children}
	</ColorPickerPrimitive.SwatchTrigger>
);

export const ColorPickerSwatch = ({
	className,
	children,
	...props
}: ColorPickerPrimitive.SwatchProps) => (
	<ColorPickerPrimitive.Swatch
		data-slot="color-picker-swatch"
		className={cn(
			"relative grid size-8 shrink-0 place-items-center overflow-hidden rounded-full shadow-[0_0_0_1px_rgba(9,9,11,0.12)] data-[state=checked]:shadow-[0_0_0_2px_#fff,0_0_0_4px_#18181b]",
			className,
		)}
		{...props}
	>
		{children}
	</ColorPickerPrimitive.Swatch>
);

export const ColorPickerSwatchIndicator = ({
	className,
	children,
	...props
}: ColorPickerPrimitive.SwatchIndicatorProps) => (
	<ColorPickerPrimitive.SwatchIndicator
		data-slot="color-picker-swatch-indicator"
		className={cn(
			"absolute inset-0 flex items-center justify-center text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] data-[state=unchecked]:hidden [&_svg]:size-3.5",
			className,
		)}
		{...props}
	>
		{children ?? <CheckIcon weight="bold" aria-hidden="true" />}
	</ColorPickerPrimitive.SwatchIndicator>
);

export const ColorPickerValueSwatch = ({
	className,
	...props
}: ColorPickerPrimitive.ValueSwatchProps) => (
	<ColorPickerPrimitive.ValueSwatch
		data-slot="color-picker-value-swatch"
		className={cn("size-full rounded-[inherit]", className)}
		{...props}
	/>
);

/** shadcn docs alias for trigger/value preview stack. */
export const ColorPickerSwatchPreview = ColorPickerTriggerSwatch;

export const ColorPickerValueText = ({
	className,
	...props
}: ColorPickerPrimitive.ValueTextProps) => (
	<ColorPickerPrimitive.ValueText
		data-slot="color-picker-value-text"
		className={cn("text-sm font-medium text-(--text-primary)", className)}
		{...props}
	/>
);

export const ColorPickerTransparencyGrid = ({
	className,
	...props
}: ColorPickerPrimitive.TransparencyGridProps) => (
	<ColorPickerPrimitive.TransparencyGrid
		data-slot="color-picker-transparency-grid"
		className={cn("size-full rounded-[inherit]", className)}
		{...props}
	/>
);

export const ColorPickerView = ({
	className,
	...props
}: ColorPickerPrimitive.ViewProps) => (
	<ColorPickerPrimitive.View
		data-slot="color-picker-view"
		className={cn("flex flex-col gap-2 [[hidden]]:hidden", className)}
		{...props}
	/>
);

export type ColorPickerPresetSwatchesProps = {
	colors: readonly string[];
	className?: string;
	swatchClassName?: string;
	renderSwatch?: (color: string) => ReactNode;
};

/** Preset swatch row (shadcn Swatch Picker demo). */
export const ColorPickerPresetSwatches = ({
	colors,
	className,
	swatchClassName,
	renderSwatch,
}: ColorPickerPresetSwatchesProps) => (
	<ColorPickerSwatchGroup className={className}>
		{colors.map((color) => (
			<ColorPickerSwatchTrigger key={color} value={parseColor(color)}>
				{renderSwatch ? (
					renderSwatch(color)
				) : (
					<ColorPickerSwatch value={parseColor(color)} className={swatchClassName}>
						<ColorPickerSwatchIndicator />
					</ColorPickerSwatch>
				)}
			</ColorPickerSwatchTrigger>
		))}
	</ColorPickerSwatchGroup>
);
